import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { PackageSearchIcon, CornerDownLeftIcon } from 'lucide-react';
import { InventoryItem } from '../data/inventory';
interface InventorySharePickerProps {
  items: InventoryItem[];
  query: string;
  activeIndex: number;
  onHover: (index: number) => void;
  onSelect: (item: InventoryItem) => void;
}
export function InventorySharePicker({
  items,
  query,
  activeIndex,
  onHover,
  onSelect
}: InventorySharePickerProps) {
  const listRef = useRef<HTMLUListElement>(null);
  // Keep the keyboard-highlighted row in view as the user arrows through.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const row = list.children[activeIndex] as HTMLElement | undefined;
    row?.scrollIntoView({
      block: 'nearest'
    });
  }, [activeIndex]);
  return <motion.div initial={{
    opacity: 0,
    y: 8,
    scale: 0.98
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1
  }} exit={{
    opacity: 0,
    y: 8,
    scale: 0.98
  }} transition={{
    duration: 0.16,
    ease: [0.23, 1, 0.32, 1]
  }} className="absolute bottom-full left-4 right-4 mb-1 z-30 bg-white rounded-2xl border border-slate-200 shadow-large overflow-hidden" data-id="element-2430" data-mp="hif1swuel084">
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-slate-100 bg-slate-50/70" data-id="element-2431" data-mp="1c1jjw4ltbjzp">
        <div className="flex items-center gap-2 min-w-0" data-id="element-2432" data-mp="1751i9dls41rl">
          <PackageSearchIcon className="w-4 h-4 text-primary-indigo flex-shrink-0" data-id="element-2433" data-mp="12b71oj1h02wq" />
          <span className="text-xs font-semibold text-slate-700 whitespace-nowrap" data-id="element-2434" data-mp="xcneygf1yn3b">
            Share from inventory
          </span>
          {query && <span className="text-xs text-slate-400 truncate" data-id="element-2435" data-mp="t9h0ilhsdplr">
              “{query}”
            </span>}
        </div>
        <span className="hidden sm:flex items-center gap-1.5 text-[11px] text-slate-400 flex-shrink-0" data-id="element-2436" data-mp="jdnrvd76ug99">
          <kbd className="px-1.5 py-0.5 bg-white border border-slate-200 rounded font-sans" data-id="element-2437" data-mp="9p8oppaed9q2">
            ↑↓
          </kbd>
          <kbd className="px-1.5 py-0.5 bg-white border border-slate-200 rounded font-sans" data-id="element-2438" data-mp="epwa9vfuwycw">
            ↵
          </kbd>
          to send
        </span>
      </div>

      {items.length === 0 ? <div className="px-4 py-6 text-center" data-id="element-2439" data-mp="4rqb3llfq6bq">
          <p className="text-sm font-medium text-slate-700" data-id="element-2440" data-mp="4si72xiy262j">
            Nothing in inventory matches that
          </p>
          <p className="text-xs text-slate-400 mt-1" data-id="element-2441" data-mp="xf2wvtjrua3z">
            Try a different model, or clear the search.
          </p>
        </div> : <ul ref={listRef} className="max-h-72 overflow-y-auto p-1.5" data-id="element-2442" data-mp="ipx00c8eovz9">
          {items.map((item, i) => {
        const isActive = i === activeIndex;
        return <li key={item.id} data-id="element-2443" data-mp="1be2lj17aap05" data-mpi={i}>
                <button type="button" onMouseEnter={() => onHover(i)} onClick={() => onSelect(item)} className={`w-full flex items-center gap-3 px-2.5 py-2 rounded-xl text-left transition-colors duration-150 ease-out ${isActive ? 'bg-indigo-50' : 'hover:bg-slate-50'}`} data-id="element-2444" data-mp="1bd11i78ais6a">
                  <img src={item.image} alt={item.title} className="w-10 h-10 rounded-lg object-cover flex-shrink-0 border border-slate-200" data-id="element-2445" data-mp="3al0z2wqw2iq7" />
                  <div className="flex-1 min-w-0" data-id="element-2446" data-mp="2hxwxz2ux2ojp">
                    <p className="text-sm font-semibold text-slate-900 truncate" data-id="element-2447" data-mp="2cytuvnyhysed">
                      {item.title}
                    </p>
                    <p className="text-xs text-slate-400 truncate" data-id="element-2448" data-mp="2qzms4gg6o1l6">
                      {item.category} ·{' '}
                      {item.talabPublished ? 'Live on Talab' : 'Inventory only'}
                    </p>
                  </div>
                  <span className="text-sm font-bold text-primary-indigo whitespace-nowrap flex-shrink-0" data-id="element-2449" data-mp="2694jhf43e5n">
                    {item.price}
                  </span>
                  {isActive && <CornerDownLeftIcon className="w-3.5 h-3.5 text-primary-indigo flex-shrink-0" data-id="element-2450" data-mp="11i113c90jwkd" />}
                </button>
              </li>;
      })}
        </ul>}
    </motion.div>;
}